import { useEffect, useRef, useState, type ComponentType, type RefObject } from 'react'
import * as surfaceAppsModule from '@shell-surface-apps'
import { useOmniAuth, useOmniSession } from '@omni/auth'
import { appHref, frontPageTiles, useMe, useMyApps } from '@omni/front-door'
import { Activity, Bell, Users, Check, ChevronDown, FileChartColumn, GraduationCap, LayoutGrid, LogOut, BookOpen, Palette, ShieldCheck, Sparkles } from 'lucide-react'
import { instanceClaude, openClaudePanel } from '../../claude/ClaudePanel'
import { activeInstance } from '../../instances'
import { productName } from '../../lib/config'
import { shellNavigate } from '../../lib/navigation'
import { DEFAULT_SURFACE_PATH, surfaces, useSurfaceVisibility, type Surface } from '../../surfaces'
import { FRONT_DOOR_APP, FRONT_PAGE_URL, frontDoor } from '../config'
import { appIcon, initials, personName } from './people'

// The one top bar of the launcher layout, on every page of the front-door
// site and of every app: the customer's logo and product name (back to the
// front page), the app switcher, Ask Claude, and the person menu (name,
// Programme, Using Claude, sign out).

// An instance may export a bell for this bar (Nateva: the notifications bell).
const NotificationBell = (surfaceAppsModule as Record<string, unknown>).NotificationBell as ComponentType | undefined

const theme = activeInstance.theme
const WORDMARK = theme.wordmarkUrl
// "Nateva Intelligence" with Nateva's wordmark reads as the wordmark plus
// "Intelligence"; a product name that does not start with the customer's
// name is written out in full beside the mark.
const SUFFIX = WORDMARK && productName.startsWith(activeInstance.displayName + ' ')
  ? productName.slice(activeInstance.displayName.length + 1)
  : null

const hasLocal = (id: string) => surfaces.some((s) => s.meta.id === id)
const pathOf = (id: string) => surfaces.find((s) => s.meta.id === id)?.meta.basePath

/** Go to a front-door-site page: in place when this build carries it, else on the front-door site. */
function goSite(id: string, sitePath: string) {
  const local = pathOf(id)
  if (local) shellNavigate(local)
  else window.location.assign(FRONT_PAGE_URL.replace(/\/$/, '') + sitePath)
}

function goFrontPage() {
  if (FRONT_DOOR_APP) window.location.assign(FRONT_PAGE_URL)
  else shellNavigate(DEFAULT_SURFACE_PATH)
}

function useDismiss(ref: RefObject<HTMLElement | null>, open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open, close, ref])
}

export function TopBar({ surface }: { surface: Surface | undefined }) {
  const canSee = useSurfaceVisibility()
  const showClaude = !!instanceClaude && canSee('learn') === true
  // On the front-door site, a page other than the launcher is named after
  // the switcher, so you always know where you are.
  const crumb = !FRONT_DOOR_APP && surface && surface.meta.id !== 'front-door' ? surface.meta.label : null

  return (
    <header className="nl-bar">
      <div className="nl-wrap nl-bar-in">
        <a
          className="nl-brand"
          href={FRONT_DOOR_APP ? FRONT_PAGE_URL : DEFAULT_SURFACE_PATH}
          onClick={(e) => {
            e.preventDefault()
            goFrontPage()
          }}
          aria-label={`${productName}, all your apps`}
        >
          {theme.logoUrl && <img src={theme.logoUrl} alt="" className="nl-brand-mark" />}
          {SUFFIX ? (
            <span className="nl-brand-name">
              <img src={WORDMARK} alt={activeInstance.displayName} className="nl-brand-word" />
              <span className="nl-brand-suffix">{SUFFIX}</span>
            </span>
          ) : (
            <span className="nl-brand-name nl-brand-text">{productName}</span>
          )}
        </a>

        <span className="nl-bar-rule" aria-hidden />
        <Switcher />
        {crumb && <span className="nl-crumb">{crumb}</span>}

        <span className="nl-bar-gap" />

        {showClaude && (
          <button type="button" className="nl-ask" onClick={openClaudePanel} title="Ask Claude about this page">
            <Sparkles size={15} />
            <span>Ask Claude</span>
          </button>
        )}
        {NotificationBell && <NotificationBell />}
        <PersonMenu />
      </div>
    </header>
  )
}

function Switcher() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(ref, open, () => setOpen(false))
  const apps = useMyApps()
  const tiles = apps.status === 'ready' ? frontPageTiles(apps.data) : []
  const appLabel = surfaces.find((s) => s.meta.id !== 'learn')?.meta.label ?? 'This app'
  const current = FRONT_DOOR_APP ? (tiles.find((t) => t.slug === FRONT_DOOR_APP)?.name ?? appLabel) : 'Apps'

  return (
    <div ref={ref} className="nl-switch">
      <button
        type="button"
        className="nl-switch-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Switch app (now: ${current})`}
        onClick={() => setOpen((v) => !v)}
      >
        <LayoutGrid size={15} />
        <span className="nl-switch-now">{current}</span>
        <ChevronDown size={14} className="nl-switch-chev" />
      </button>
      {open && (
        <div className="nl-menu nl-switch-menu" role="menu" aria-label={`${productName} apps`}>
          <p className="nl-menu-label">Your apps</p>
          <a
            role="menuitem"
            className="nl-menu-app"
            href={FRONT_DOOR_APP ? FRONT_PAGE_URL : DEFAULT_SURFACE_PATH}
            onClick={(e) => {
              e.preventDefault()
              setOpen(false)
              goFrontPage()
            }}
          >
            <span className="nl-menu-icon nl-menu-icon-home">
              <LayoutGrid size={16} />
            </span>
            <span className="nl-menu-app-text">
              <span className="nl-menu-app-name">All apps</span>
              <span className="nl-menu-app-addr">{frontDoor.domain}</span>
            </span>
            {!FRONT_DOOR_APP && <Check size={15} className="nl-menu-here" aria-label="You are here" />}
          </a>
          {apps.status === 'loading' && <p className="nl-menu-note">Loading your apps…</p>}
          {apps.status === 'error' && (
            <p className="nl-menu-note">
              {apps.error.missing ? 'Your apps show here once access is switched on.' : 'Your apps could not load just now.'}
            </p>
          )}
          {apps.status === 'ready' && tiles.length === 0 && <p className="nl-menu-note">No apps yet.</p>}
          {tiles.map((t) => {
            const href = appHref(t, frontDoor.domain)
            const here = t.slug === FRONT_DOOR_APP
            const Icon = appIcon(t)
            const off = (t.status === 'building' && !t.url) || !href
            const inner = (
              <>
                <span className="nl-menu-icon">
                  <Icon size={16} />
                </span>
                <span className="nl-menu-app-text">
                  <span className="nl-menu-app-name">{t.name}</span>
                  <span className="nl-menu-app-addr">{off ? 'Coming soon' : new URL(href!).host}</span>
                </span>
                {here && <Check size={15} className="nl-menu-here" aria-label="You are here" />}
              </>
            )
            return off ? (
              <span key={t.slug} role="menuitem" aria-disabled="true" className="nl-menu-app nl-menu-app-off">
                {inner}
              </span>
            ) : (
              <a key={t.slug} role="menuitem" className="nl-menu-app" href={href!} aria-current={here ? 'page' : undefined}>
                {inner}
              </a>
            )
          })}
        </div>
      )}
    </div>
  )
}

function PersonMenu() {
  const [open, setOpen] = useState(false)
  const canSee = useSurfaceVisibility()
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(ref, open, () => setOpen(false))
  const { signOut } = useOmniAuth()
  const { primaryEmail } = useOmniSession()
  const me = useMe()
  const email = (me.status === 'ready' ? me.data?.email : null) ?? primaryEmail ?? null
  const name = personName(me.status === 'ready' ? me.data?.displayName : null, email)
  const pick = (fn: () => void) => () => {
    setOpen(false)
    fn()
  }

  return (
    <div ref={ref} className="nl-person">
      <button
        type="button"
        className="nl-avatar"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={name ? `${name}, your menu` : 'Your menu'}
        onClick={() => setOpen((v) => !v)}
      >
        {initials(name, email)}
      </button>
      {open && (
        <div className="nl-menu nl-person-menu" role="menu" aria-label="Your menu">
          <div className="nl-person-head">
            <span className="nl-avatar nl-avatar-lg" aria-hidden>
              {initials(name, email)}
            </span>
            <span className="nl-person-who">
              <span className="nl-person-name">{name ?? 'Signed in'}</span>
              {email && <span className="nl-person-email">{email}</span>}
            </span>
          </div>
          <div className="nl-menu-sep" />
          <button type="button" role="menuitem" className="nl-menu-item" onClick={pick(goFrontPage)}>
            <LayoutGrid size={16} /> All apps
          </button>
          {(hasLocal('home') || FRONT_DOOR_APP) && (
            <button type="button" role="menuitem" className="nl-menu-item" onClick={pick(() => goSite('home', '/home'))}>
              <BookOpen size={16} /> Programme
            </button>
          )}
          {hasLocal('reports') && (
            <button type="button" role="menuitem" className="nl-menu-item" onClick={pick(() => goSite('reports', '/reports'))}>
              <FileChartColumn size={16} /> Reports
            </button>
          )}
          {hasLocal('people') && (
            <button type="button" role="menuitem" className="nl-menu-item" onClick={pick(() => goSite('people', '/people'))}>
              <Users size={16} /> People and access
            </button>
          )}
          {hasLocal('team') && (
            <button type="button" role="menuitem" className="nl-menu-item" onClick={pick(() => goSite('team', '/team'))}>
              <Activity size={16} /> Team
            </button>
          )}
          {hasLocal('brand') && (
            <button type="button" role="menuitem" className="nl-menu-item" onClick={pick(() => goSite('brand', '/brand'))}>
              <Palette size={16} /> Brand
            </button>
          )}
          {hasLocal('health') && canSee('health') === true && (
            <button type="button" role="menuitem" className="nl-menu-item" onClick={pick(() => goSite('health', '/health'))}>
              <ShieldCheck size={16} /> Health
            </button>
          )}
          {hasLocal('notifications') && (
            <button type="button" role="menuitem" className="nl-menu-item" onClick={pick(() => goSite('notifications', '/notifications'))}>
              <Bell size={16} /> Notifications
            </button>
          )}
          {hasLocal('learn') && (
            <button type="button" role="menuitem" className="nl-menu-item" onClick={pick(() => goSite('learn', '/learn'))}>
              <GraduationCap size={16} /> Using Claude
            </button>
          )}
          <div className="nl-menu-sep" />
          <button type="button" role="menuitem" className="nl-menu-item nl-menu-out" onClick={pick(() => void signOut())}>
            <LogOut size={16} /> Sign out
          </button>
        </div>
      )}
    </div>
  )
}
